/**
 * Ability Effect Factory
 *
 * Creates visual effects for dynamic abilities by composing
 * element configurations with delivery renderers.
 *
 * Updated L4-EFFECT-006: Supports sprite-based effects from Effect Gallery
 * when effectKeys are provided, falls back to procedural graphics otherwise.
 */

import * as PIXI from 'pixi.js';
import { getElementConfig, getScaledElementConfig, getSizeMultiplier, type ElementType, type VisualImportance } from './element-effect-configs.js';
import {
  renderMeleeEffect,
  renderProjectileEffect,
  renderBreathEffect,
  renderBeamEffect,
  renderAOEEffect,
  renderBuffEffect,
  renderStrikeEffect,
  renderBreathParticleEffect,
  type DeliveryType,
  type DeliveryEffectParams,
  type AOEEffectParams,
  type BuffEffectParams,
  type StrikeEffectParams
} from './delivery-effect-renderer.js';
import {
  renderSpriteEffect,
  renderImpactEffect,
  hasEffect,
  type DeliveryType as SpriteDeliveryType
} from './effect-sprite-renderer.js';
import {
  renderHexGroundMarker,
  pixelsToHexRadius
} from './ground-marker-renderer.js';

export interface AbilityEffectRequest {
  abilityId: string;
  abilityName: string;
  element: string;
  delivery: string;
  effects: Array<{
    type: string;
    value?: number;
    duration?: number;
    ccType?: string;
    stat?: string;
    modifier?: number;
  }>;

  // Positions
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;

  // Optional AOE
  aoeRadius?: number;

  // Target type for buff/debuff direction
  targetType?: 'enemy' | 'ally' | 'self' | 'area';

  // Strike visual origin for strike delivery (L4-EFFECT-001)
  strikeOrigin?: 'sky' | 'ground';

  // Screen height for strike effects (L3-LIGHTNING-SKY)
  screenHeight?: number;

  // Effect Gallery keys (L4-EFFECT-006)
  effectKeys?: {
    primary?: string;
    impact?: string;
  };

  // Visual importance for particle scaling (L4-VIS-004)
  visualImportance?: VisualImportance;
}

export interface AbilityEffectResult {
  completed: boolean;
  element: ElementType;
  delivery: DeliveryType;
}

/**
 * Main factory class for creating ability visual effects
 */
export class AbilityEffectFactory {
  private container: PIXI.Container;
  private effectsLayer: PIXI.Container;

  constructor(parentContainer: PIXI.Container) {
    this.container = parentContainer;

    // Create effects layer if not exists
    this.effectsLayer = new PIXI.Container();
    this.effectsLayer.label = 'ability-effects-layer';
    this.container.addChild(this.effectsLayer);
  }

  /**
   * Play ability effect with appropriate visuals
   *
   * L4-EFFECT-006: Now checks for effectKeys first - if available and cached,
   * uses sprite-based rendering from Effect Gallery. Falls back to procedural
   * graphics if no effectKeys or sprites not loaded.
   */
  playAbilityEffect(request: AbilityEffectRequest): Promise<AbilityEffectResult> {
    return new Promise((resolve) => {
      const element = (request.element || 'physical') as ElementType;
      const delivery = (request.delivery || 'melee') as DeliveryType;
      const importance = request.visualImportance || 'minor';
      // L4-VIS-004: Get scaled config based on visual importance
      const config = getScaledElementConfig(element, importance);
      // L4-VIS-005: Get size multiplier for visual scaling
      const sizeMultiplier = getSizeMultiplier(importance);

      console.log(`[AbilityEffectFactory] ${request.abilityName}: importance=${importance}, particles=${config.particleCount}, size=${sizeMultiplier}x`);

      const onComplete = () => {
        // Play impact effect if available
        if (request.effectKeys?.impact && hasEffect(request.effectKeys.impact)) {
          renderImpactEffect(
            this.effectsLayer,
            request.effectKeys.impact,
            request.targetX,
            request.targetY
          );
        }

        resolve({
          completed: true,
          element,
          delivery
        });
      };

      // L4-EFFECT-006: Try sprite-based rendering if effectKeys available
      if (request.effectKeys?.primary && hasEffect(request.effectKeys.primary)) {
        console.log(`[AbilityEffectFactory] Using sprite effect: ${request.effectKeys.primary}`);
        const rendered = renderSpriteEffect(this.effectsLayer, {
          effectKey: request.effectKeys.primary,
          delivery: delivery as SpriteDeliveryType,
          startX: request.startX,
          startY: request.startY,
          targetX: request.targetX,
          targetY: request.targetY,
          aoeRadius: request.aoeRadius,
          strikeOrigin: request.strikeOrigin,
          onComplete
        });

        if (rendered) {
          // Successfully using sprite effect, handle DoT/status as usual
          this.handleSecondaryEffects(request, element);
          return;
        }
        // Fall through to procedural if sprite render failed
        console.warn(`[AbilityEffectFactory] Sprite render failed, falling back to procedural`);
      }

      // Choose renderer based on delivery type (procedural fallback)
      switch (delivery) {
        case 'melee':
          renderMeleeEffect({
            container: this.effectsLayer,
            startX: request.startX,
            startY: request.startY,
            targetX: request.targetX,
            targetY: request.targetY,
            element,
            onComplete
          });
          break;

        case 'projectile':
          renderProjectileEffect({
            container: this.effectsLayer,
            startX: request.startX,
            startY: request.startY,
            targetX: request.targetX,
            targetY: request.targetY,
            element,
            onComplete
          });
          break;

        case 'breath':
          // L4-VIS-002: Show ground marker for breath AOE
          // L4-VIS-005: Scale radius by importance
          if (request.aoeRadius) {
            const scaledRadius = request.aoeRadius * sizeMultiplier;
            const breathRadiusHexes = pixelsToHexRadius(scaledRadius);
            renderHexGroundMarker(this.effectsLayer, {
              centerX: request.targetX,
              centerY: request.targetY,
              radiusHexes: breathRadiusHexes,
              elementColor: config.primaryColor,
              duration: 1000, // 200ms appear + 400ms effect + 400ms fade
              appearDelay: 0,
              shape: 'hex',
              pulseAnimation: true
            });
          }

          renderBreathEffect({
            container: this.effectsLayer,
            startX: request.startX,
            startY: request.startY,
            targetX: request.targetX,
            targetY: request.targetY,
            element,
            onComplete
          });
          break;

        case 'beam':
          renderBeamEffect({
            container: this.effectsLayer,
            startX: request.startX,
            startY: request.startY,
            targetX: request.targetX,
            targetY: request.targetY,
            element,
            onComplete
          });
          break;

        case 'aoe':
          // L4-VIS-002: Show ground marker before AOE damage
          // L4-VIS-005: Scale radius by importance
          {
            const baseRadius = request.aoeRadius || 60;
            const scaledRadius = baseRadius * sizeMultiplier;

            if (request.aoeRadius) {
              const radiusHexes = pixelsToHexRadius(scaledRadius);
              renderHexGroundMarker(this.effectsLayer, {
                centerX: request.targetX,
                centerY: request.targetY,
                radiusHexes,
                elementColor: config.primaryColor,
                duration: 1100, // 200ms appear + 500ms effect + 400ms fade
                appearDelay: 0,
                shape: 'hex',
                pulseAnimation: true
              });
            }

            renderAOEEffect({
              container: this.effectsLayer,
              centerX: request.targetX,
              centerY: request.targetY,
              radius: scaledRadius,
              element,
              onComplete
            });
          }
          break;

        case 'buff':
          // Determine if buff or debuff based on effects
          const hasDebuff = request.effects.some(e =>
            e.type === 'debuff' || e.type === 'cc' || e.type === 'dot'
          );
          const isBuff = !hasDebuff && request.targetType !== 'enemy';

          renderBuffEffect({
            container: this.effectsLayer,
            targetX: request.targetX,
            targetY: request.targetY,
            element,
            isBuff,
            onComplete
          });
          break;

        case 'strike':
          // L4-VIS-002: Show ground marker before strike hits
          // L4-VIS-005: Scale radius by importance
          {
            const baseStrikeRadius = request.aoeRadius || 32; // Default 1 hex
            const scaledStrikeRadius = baseStrikeRadius * sizeMultiplier;
            const strikeRadiusHexes = pixelsToHexRadius(scaledStrikeRadius);

            renderHexGroundMarker(this.effectsLayer, {
              centerX: request.targetX,
              centerY: request.targetY,
              radiusHexes: strikeRadiusHexes,
              elementColor: config.primaryColor,
              duration: 900, // 200ms appear + 300ms telegraph + 400ms fade
              appearDelay: 0,
              shape: 'hex',
              pulseAnimation: true
            });
          }

          // L3-LIGHTNING-SKY: Lightning from sky effect
          renderStrikeEffect({
            container: this.effectsLayer,
            targetX: request.targetX,
            targetY: request.targetY,
            element,
            strikeOrigin: request.strikeOrigin || 'sky',
            screenHeight: request.screenHeight || 600,
            onComplete
          });
          break;

        case 'breath-particle':
          // L3-PARTICLE-BREATH: Massive fan of 25 projectiles
          renderBreathParticleEffect({
            container: this.effectsLayer,
            startX: request.startX,
            startY: request.startY,
            targetX: request.targetX,
            targetY: request.targetY,
            element,
            onComplete
          });
          break;

        default:
          // Fallback to melee
          renderMeleeEffect({
            container: this.effectsLayer,
            startX: request.startX,
            startY: request.startY,
            targetX: request.targetX,
            targetY: request.targetY,
            element,
            onComplete
          });
      }

      // If ability has DoT effect, schedule periodic particles
      const dotEffect = request.effects.find(e => e.type === 'dot');
      if (dotEffect && dotEffect.duration) {
        this.scheduleDoTParticles(request.targetX, request.targetY, element, dotEffect.duration);
      }

      // If ability has buff/debuff, show status indicator
      const statusEffect = request.effects.find(e =>
        e.type === 'buff' || e.type === 'debuff' || e.type === 'hot'
      );
      if (statusEffect && statusEffect.duration) {
        this.showStatusIndicator(
          request.targetX,
          request.targetY,
          element,
          statusEffect.type === 'buff' || statusEffect.type === 'hot'
        );
      }
    });
  }

  /**
   * Handle secondary visual effects (DoT particles, status indicators)
   * Called by both sprite-based and procedural rendering paths
   */
  private handleSecondaryEffects(request: AbilityEffectRequest, element: ElementType): void {
    // If ability has DoT effect, schedule periodic particles
    const dotEffect = request.effects.find(e => e.type === 'dot');
    if (dotEffect && dotEffect.duration) {
      this.scheduleDoTParticles(request.targetX, request.targetY, element, dotEffect.duration);
    }

    // If ability has buff/debuff, show status indicator
    const statusEffect = request.effects.find(e =>
      e.type === 'buff' || e.type === 'debuff' || e.type === 'hot'
    );
    if (statusEffect && statusEffect.duration) {
      this.showStatusIndicator(
        request.targetX,
        request.targetY,
        element,
        statusEffect.type === 'buff' || statusEffect.type === 'hot'
      );
    }
  }

  /**
   * Schedule periodic DoT particle effects
   */
  private scheduleDoTParticles(x: number, y: number, element: ElementType, duration: number): void {
    const config = getElementConfig(element);
    const tickInterval = 1000; // 1 second between ticks
    let elapsed = 0;

    const tick = () => {
      if (elapsed >= duration * 1000) return;

      // Create small particle burst
      this.createParticleBurst(x, y, element, 5);

      elapsed += tickInterval;
      setTimeout(tick, tickInterval);
    };

    // Start after a short delay
    setTimeout(tick, tickInterval);
  }

  /**
   * Show status effect indicator (aura around target)
   */
  private showStatusIndicator(x: number, y: number, element: ElementType, isBuff: boolean): void {
    const config = getElementConfig(element);

    const indicator = new PIXI.Graphics();
    indicator.position.set(x, y);
    indicator.blendMode = config.blendMode as PIXI.BLEND_MODES;
    this.effectsLayer.addChild(indicator);

    let frame = 0;
    const animate = () => {
      frame++;
      const pulse = Math.sin(frame * 0.1) * 0.3 + 0.7;

      indicator.clear();
      indicator.lineStyle(2, isBuff ? config.primaryColor : 0xFF0000, pulse * 0.5);
      indicator.drawCircle(0, 0, 25 + Math.sin(frame * 0.05) * 3);

      if (frame < 180) { // 3 seconds at 60fps
        requestAnimationFrame(animate);
      } else {
        this.effectsLayer.removeChild(indicator);
        indicator.destroy();
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Create particle burst at position
   */
  private createParticleBurst(x: number, y: number, element: ElementType, count: number): void {
    const config = getElementConfig(element);

    for (let i = 0; i < count; i++) {
      const particle = new PIXI.Graphics();
      particle.beginFill(config.primaryColor);
      particle.drawCircle(0, 0, 2 + Math.random() * 2);
      particle.endFill();
      particle.position.set(x, y);
      particle.blendMode = config.blendMode as PIXI.BLEND_MODES;
      this.effectsLayer.addChild(particle);

      const angle = Math.random() * Math.PI * 2;
      const speed = 1 + Math.random() * 2;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed - 1; // Slight upward bias
      let life = 30;

      const animate = () => {
        life--;
        particle.x += vx;
        particle.y += vy;
        particle.alpha = life / 30;

        if (life > 0) {
          requestAnimationFrame(animate);
        } else {
          this.effectsLayer.removeChild(particle);
          particle.destroy();
        }
      };

      requestAnimationFrame(animate);
    }
  }

  /**
   * Clean up effects layer
   */
  dispose(): void {
    this.effectsLayer.removeChildren();
    this.container.removeChild(this.effectsLayer);
    this.effectsLayer.destroy({ children: true });
  }
}

// Singleton instance (created when combat visualization initializes)
let factoryInstance: AbilityEffectFactory | null = null;

export function initializeAbilityEffectFactory(container: PIXI.Container): AbilityEffectFactory {
  if (factoryInstance) {
    factoryInstance.dispose();
  }
  factoryInstance = new AbilityEffectFactory(container);
  return factoryInstance;
}

export function getAbilityEffectFactory(): AbilityEffectFactory | null {
  return factoryInstance;
}
